
import * as React from "react"

import { cn } from "@/lib/utils"

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    hover?: boolean
    interactive?: boolean
  }
>(({ className, hover = false, interactive = false, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-lg bg-surface-primary text-card-foreground transition-all duration-200 ease-out max-w-none border border-virgilio-border shadow-calendly",
      hover && "hover:-translate-y-0.5 hover:shadow-[var(--shadow-sm)] hover:border-virgilio-purple/30",
      interactive && "cursor-pointer hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-virgilio-purple focus-visible:ring-offset-2 ring-offset-background focus-visible:shadow-[var(--shadow-md)]",
      className
    )}
    {...(interactive ? { tabIndex: 0 } : {})}
    {...props}
  />
))
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1 p-layout-md", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement> & {
    withPeriod?: boolean
  }
>(({ className, withPeriod = true, children, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-lg font-poppins font-semibold tracking-tight leading-tight text-virgilio-text",
      className
    )}
    {...props}
  >
    {children}
    {withPeriod && typeof children === 'string' && (
      <span className="text-[#d7c5fb]">.</span>
    )}
  </h3>
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-xs text-text-secondary leading-normal", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-layout-md pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-layout-md pt-0 gap-sm", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
